// Thin client over apiFetch for convorcher's /admin/* character endpoints —
// the "brain/voice" management surface (ENG-833).
//
// Every call is a same-origin request to /admin/... which nginx (deploy,
// `location /admin/`) or the Vite dev proxy (`/admin`) forwards to convorcher
// with `X-API-Key ${ADMIN_APIKEY}` injected server-side. The browser never
// holds the admin key. This mirrors the existing /api/* channel exactly; the
// only difference is the namespace convorcher registers the routes under
// (main.go: /admin/characters/...).
//
// apiFetch (apiClient.js) already carries the 401→/auth/refresh→retry and
// transient-network-retry machinery, so this module is a typed wrapper with no
// fetch logic of its own. The request shapes are factored into the pure
// `requests` map below so the path/method/body contract is unit-testable
// without a network — see tests/unit/convorcherAdmin.test.mjs.

import { apiFetch } from './apiClient.js'

const BASE = '/admin/characters'

// characterPath builds /admin/characters/{id}[/{sub}]. The id is the character
// UUID (convorcher's admin routes key on the UUID, not the slug); encode it so
// a stray URL-significant char can't break routing.
export function characterPath(id, sub) {
  const root = `${BASE}/${encodeURIComponent(id)}`
  return sub ? `${root}/${sub}` : root
}

// Pure request specs: { method, path, body? }. No I/O — safe to unit-test.
export const requests = {
  listBrainCharacters: () => ({ method: 'GET', path: BASE }),
  createBrainCharacter: ({ name, personality = '', voiceId = '' }) => ({
    method: 'POST',
    path: BASE,
    body: { name, personality, voiceId },
  }),
  getBrainCharacter: (id) => ({ method: 'GET', path: characterPath(id) }),
  getAnimConfig: (id) => ({ method: 'GET', path: characterPath(id, 'anim-config') }),
  updatePersonality: (id, personality) => ({
    method: 'PUT',
    path: characterPath(id),
    body: { personality },
  }),
  setVoice: (id, voiceId) => ({
    method: 'PUT',
    path: characterPath(id, 'voice'),
    body: { voiceId },
  }),
  setVocalCues: (id, vocalCues) => ({
    method: 'PUT',
    path: characterPath(id, 'vocal-cues'),
    body: { vocalCues },
  }),
  setGestures: (id, gestures) => ({
    method: 'PUT',
    path: characterPath(id, 'gestures'),
    body: { gestures },
  }),
  setGestureSynonyms: (id, gestureSynonyms) => ({
    method: 'PUT',
    path: characterPath(id, 'gesture-synonyms'),
    body: { gestureSynonyms },
  }),

  // ── Similarity threshold (ENG-862) ─────────────────────────────────────────
  // Per-character cached-response match strictness. null = inherit the global
  // setting; the GET also returns effectiveThreshold (what matching uses now).
  getSimilarityThreshold: (id) => ({ method: 'GET', path: characterPath(id, 'similarity-threshold') }),
  setSimilarityThreshold: (id, similarityThreshold) => ({
    method: 'PUT',
    path: characterPath(id, 'similarity-threshold'),
    body: { similarityThreshold },
  }),

  // ── A2F model selection ────────────────────────────────────────────────────
  // Which Audio2Face model a character's sessions route to. The options are
  // the server's A2F_ENDPOINTS keys; the per-character override substitutes
  // one for the character's own name at session creation. Convorcher builds
  // that predate these routes 404 the options call — a2fStore.js maps exactly
  // that to a visible "unsupported" state.
  getA2fOptions: () => ({ method: 'GET', path: '/admin/a2f/options' }),
  getA2fCharacter: (id) => ({ method: 'GET', path: characterPath(id, 'a2f-character') }),
  setA2fCharacter: (id, a2fCharacter) => ({
    method: 'PUT',
    path: characterPath(id, 'a2f-character'),
    body: { a2fCharacter },
  }),

  // ── Prefabs (ENG-834) ──────────────────────────────────────────────────────
  // convorcher's phrase-trigger rows: what makes a prefab *fire* in a live
  // conversation (the animation bundle itself lives in AAM, keyed by the same
  // tag). Names are unique per character (characterName = lowercased character
  // name — convorcher migration 00022). NOTE the trailing slash on the
  // collection path: convorcher registers the handler at "/admin/prefabs/".
  // phrases / notFor are jsonb string arrays (convorcher PRs #98/#99). The
  // API dual-accepts string-or-array on write during the transition, but new
  // writes always send arrays — the accept-string shim is scheduled for
  // deletion (ENG-834 follow-up).
  listPrefabs: () => ({ method: 'GET', path: '/admin/prefabs/' }),
  createPrefab: ({ characterName, name, phrases = [], alternate = '', notFor = [] }) => ({
    method: 'POST',
    path: '/admin/prefabs/',
    body: { characterName, name, phrases, alternate, notFor },
  }),
  updatePrefab: (id, { phrases = [], alternate = '', notFor = [] }) => ({
    method: 'PUT',
    path: `/admin/prefabs/${encodeURIComponent(id)}`,
    body: { phrases, alternate, notFor },
  }),
  deletePrefab: (id) => ({ method: 'DELETE', path: `/admin/prefabs/${encodeURIComponent(id)}` }),
  setPrefabStories: (id, storyIds) => ({
    method: 'PUT',
    path: `/admin/prefabs/${encodeURIComponent(id)}/stories`,
    body: { storyIds },
  }),
  // Story list — used to auto-link a reflected prefab to its character's
  // stories (a prefab only enters a session's validPrefabs via story links).
  listStories: () => ({ method: 'GET', path: '/admin/stories' }),

  // ── Stories (ENG-853) ──────────────────────────────────────────────────────
  // Standard-tier /api/stories/* is raw-YAML in/out (NOT JSON): import + validate
  // send the YAML document as the request body with Content-Type application/yaml;
  // export returns YAML text (responseType:'text' — see apiClient). The structured
  // detail view is the admin route. filename/id are the convorcher keys — never
  // rename them client-side.
  getStoryDetail: (filename) => ({ method: 'GET', path: `/admin/stories/${encodeURIComponent(filename)}/detail` }),
  getStorySource: (filename) => ({ method: 'GET', path: `/api/stories/${encodeURIComponent(filename)}` }),
  validateStory: (yaml) => ({
    method: 'POST',
    path: '/api/stories/validate',
    body: yaml,
    headers: { 'Content-Type': 'application/yaml' },
  }),
  importStory: (yaml, filename) => ({
    method: 'POST',
    path: `/api/stories/import?filename=${encodeURIComponent(filename)}`,
    body: yaml,
    headers: { 'Content-Type': 'application/yaml' },
  }),
  exportStory: (filename) => ({
    method: 'GET',
    path: `/api/stories/${encodeURIComponent(filename)}/export`,
    responseType: 'text',
  }),
  deleteStory: (filename) => ({ method: 'DELETE', path: `/api/stories/${encodeURIComponent(filename)}` }),

  // ── Cached responses (ENG-853 P3) ───────────────────────────────────────────
  // Admin-tier canned replies + pre-generated audio, matched by phrases (jsonb
  // arrays, like prefabs). Collection path carries convorcher's trailing slash.
  listCachedResponses: () => ({ method: 'GET', path: '/admin/cached-responses/' }),
  getCachedResponse: (id) => ({ method: 'GET', path: `/admin/cached-responses/${encodeURIComponent(id)}` }),
  createCachedResponse: ({ name, phrases = [], response = '', notFor = [], animations = '' }) => ({
    method: 'POST',
    path: '/admin/cached-responses/',
    body: { name, phrases, response, notFor, animations },
  }),
  updateCachedResponse: (id, { phrases = [], response = '', notFor = [], animations = '' }) => ({
    method: 'PUT',
    path: `/admin/cached-responses/${encodeURIComponent(id)}`,
    body: { phrases, response, notFor, animations },
  }),
  deleteCachedResponse: (id) => ({ method: 'DELETE', path: `/admin/cached-responses/${encodeURIComponent(id)}` }),
  setCachedResponseStories: (id, storyIds) => ({
    method: 'PUT',
    path: `/admin/cached-responses/${encodeURIComponent(id)}/stories`,
    body: { storyIds },
  }),
  regenerateCachedResponse: (id) => ({ method: 'POST', path: `/admin/cached-responses/${encodeURIComponent(id)}/regenerate` }),

  // Per-linked-story pre-generated audio (ENG-861). Audio is keyed per
  // (response, character, version) — the DETAIL payload's stories[] carries
  // characterName/characterVersion/hasAudio (the list zero-fills them). GET
  // returns PCM-as-WAV; fetch it as a blob through apiFetch (a bare Audio.src
  // would bypass the 401-refresh machinery — see assetUrl.js).
  getCachedResponseAudio: (id, { character, version }) => ({
    method: 'GET',
    path: `/admin/cached-responses/${encodeURIComponent(id)}/audio?character=${encodeURIComponent(character)}&version=${encodeURIComponent(version)}`,
    responseType: 'blob',
  }),
  deleteCachedResponseAudio: (id, { character, version }) => ({
    method: 'DELETE',
    path: `/admin/cached-responses/${encodeURIComponent(id)}/audio?character=${encodeURIComponent(character)}&version=${encodeURIComponent(version)}`,
  }),
}

// run executes a pure request spec through apiFetch, threading an optional
// AbortSignal.
function run(spec, opts = {}) {
  const init = { method: spec.method }
  if (spec.body !== undefined) init.body = spec.body
  if (spec.headers) init.headers = spec.headers
  if (spec.responseType) init.responseType = spec.responseType
  if (opts.signal) init.signal = opts.signal
  return apiFetch(spec.path, init)
}

// convorcherAdmin — the callable surface used by GASS UI/state.
export const convorcherAdmin = {
  listBrainCharacters: (opts) => run(requests.listBrainCharacters(), opts),
  createBrainCharacter: (payload, opts) => run(requests.createBrainCharacter(payload), opts),
  getBrainCharacter: (id, opts) => run(requests.getBrainCharacter(id), opts),
  getAnimConfig: (id, opts) => run(requests.getAnimConfig(id), opts),
  updatePersonality: (id, personality, opts) => run(requests.updatePersonality(id, personality), opts),
  setVoice: (id, voiceId, opts) => run(requests.setVoice(id, voiceId), opts),
  setVocalCues: (id, vocalCues, opts) => run(requests.setVocalCues(id, vocalCues), opts),
  setGestures: (id, gestures, opts) => run(requests.setGestures(id, gestures), opts),
  setGestureSynonyms: (id, gestureSynonyms, opts) => run(requests.setGestureSynonyms(id, gestureSynonyms), opts),
  getSimilarityThreshold: (id, opts) => run(requests.getSimilarityThreshold(id), opts),
  setSimilarityThreshold: (id, similarityThreshold, opts) => run(requests.setSimilarityThreshold(id, similarityThreshold), opts),
  getA2fOptions: (opts) => run(requests.getA2fOptions(), opts),
  getA2fCharacter: (id, opts) => run(requests.getA2fCharacter(id), opts),
  setA2fCharacter: (id, a2fCharacter, opts) => run(requests.setA2fCharacter(id, a2fCharacter), opts),
  listPrefabs: (opts) => run(requests.listPrefabs(), opts),
  createPrefab: (payload, opts) => run(requests.createPrefab(payload), opts),
  updatePrefab: (id, payload, opts) => run(requests.updatePrefab(id, payload), opts),
  deletePrefab: (id, opts) => run(requests.deletePrefab(id), opts),
  setPrefabStories: (id, storyIds, opts) => run(requests.setPrefabStories(id, storyIds), opts),
  listStories: (opts) => run(requests.listStories(), opts),
  getStoryDetail: (filename, opts) => run(requests.getStoryDetail(filename), opts),
  getStorySource: (filename, opts) => run(requests.getStorySource(filename), opts),
  validateStory: (yaml, opts) => run(requests.validateStory(yaml), opts),
  importStory: (yaml, filename, opts) => run(requests.importStory(yaml, filename), opts),
  exportStory: (filename, opts) => run(requests.exportStory(filename), opts),
  deleteStory: (filename, opts) => run(requests.deleteStory(filename), opts),
  listCachedResponses: (opts) => run(requests.listCachedResponses(), opts),
  getCachedResponse: (id, opts) => run(requests.getCachedResponse(id), opts),
  createCachedResponse: (payload, opts) => run(requests.createCachedResponse(payload), opts),
  updateCachedResponse: (id, payload, opts) => run(requests.updateCachedResponse(id, payload), opts),
  deleteCachedResponse: (id, opts) => run(requests.deleteCachedResponse(id), opts),
  setCachedResponseStories: (id, storyIds, opts) => run(requests.setCachedResponseStories(id, storyIds), opts),
  regenerateCachedResponse: (id, opts) => run(requests.regenerateCachedResponse(id), opts),
  getCachedResponseAudio: (id, cv, opts) => run(requests.getCachedResponseAudio(id, cv), opts),
  deleteCachedResponseAudio: (id, cv, opts) => run(requests.deleteCachedResponseAudio(id, cv), opts),
}
